import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bot, MousePointerClick, Bell, BellRing, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Admin-Statistik zur Assistenz-Nutzung (Feb-2026, User-Wunsch).
 * Zeigt, welche Vorschlags-Chips, CTAs und Nudges am häufigsten genutzt werden,
 * damit die Vorschläge datenbasiert optimiert werden können.
 */

const ROLE_LABELS = {
    admin: "Admin", b2b: "Händler", b2c: "Kunde",
    vermittler: "Vermittler", zweiradverkaeufer: "Zweiradverkäufer", service_provider: "Dienstleister",
};

function StatCard({ icon: Icon, label, value, testId, accent }) {
    return (
        <div className={`rounded-sm border p-4 ${accent || "bg-white border-gray-200"}`} data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <Icon className="w-5 h-5 text-gray-500" />
            </div>
            <div className="text-2xl font-black font-mono-num leading-none">{value ?? 0}</div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mt-1">{label}</div>
        </div>
    );
}

function RankList({ title, items, valueKey = "count", emptyText, testId }) {
    const max = Math.max(1, ...items.map((i) => i[valueKey] || 0));
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid={testId}>
            <h2 className="font-display text-base font-black mb-3">{title}</h2>
            {items.length === 0 ? (
                <div className="text-sm text-gray-500" data-testid={`${testId}-empty`}>{emptyText}</div>
            ) : (
                <div className="space-y-2">
                    {items.map((it, i) => (
                        <div key={i} className="flex items-center gap-3" data-testid={`${testId}-row-${i}`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-[#111827] truncate" title={it.label}>{it.label}</div>
                                <div className="mt-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-[#FFCC00]" style={{ width: `${((it[valueKey] || 0) / max) * 100}%` }} />
                                </div>
                            </div>
                            <div className="text-sm font-black font-mono-num w-10 text-right">{it[valueKey] || 0}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function TrendChart({ trend }) {
    if (!trend || trend.length === 0) {
        return <div className="text-sm text-gray-500" data-testid="trend-chart-empty">Noch keine Daten im Zeitraum.</div>;
    }
    const max = Math.max(1, ...trend.map((t) => t.count));
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5 mb-5" data-testid="trend-chart">
            <h2 className="font-display text-base font-black mb-3">Nutzungs-Trend (Ereignisse / Tag)</h2>
            <div className="flex items-end gap-1 h-40" data-testid="trend-chart-bars">
                {trend.map((t, i) => (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end group min-w-0" title={`${t.date}: ${t.count}`}>
                        <div className="text-[9px] font-mono-num text-gray-400 mb-0.5 opacity-0 group-hover:opacity-100">{t.count}</div>
                        <div className="w-full bg-[#FFCC00] hover:bg-[#FFD633] rounded-t-sm transition-all" style={{ height: `${Math.max(4, (t.count / max) * 100)}%` }} data-testid={`trend-bar-${i}`} />
                    </div>
                ))}
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 font-mono-num mt-2">
                <span>{trend[0]?.date}</span>
                <span>{trend[trend.length - 1]?.date}</span>
            </div>
        </div>
    );
}

const RANGE_OPTIONS = [
    { value: 7, label: "7 Tage" },
    { value: 30, label: "30 Tage" },
    { value: 90, label: "90 Tage" },
    { value: 0, label: "Gesamt" },
];

export default function AdminAssistantStatsPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);
    const [range, setRange] = useState(30);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "admin") { navigate("/"); return; }
        let cancelled = false;
        setLoadingStats(true);
        (async () => {
            try {
                const { data } = await api.get("/assistant/stats", { params: { days: range } });
                if (!cancelled) setData(data);
            } catch (e) {
                console.error("Assistant stats load failed", e);
            } finally {
                if (!cancelled) setLoadingStats(false);
            }
        })();
        return () => { cancelled = true; };
    }, [user, loading, navigate, range]);

    if (loading || !user) return <div className="p-12">Lädt...</div>;

    const totals = data?.totals || {};
    const byRole = data?.by_role || [];

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="admin-assistant-stats-page">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">
                <Bot className="w-4 h-4" /> Admin · KI-Assistent
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-2">Assistenz-Nutzung</h1>
            <p className="text-gray-600 mb-5">Welche Vorschläge, Aktionen und Hinweise nutzen die Anwender am häufigsten?</p>

            {/* Zeitraum-Filter */}
            <div className="flex items-center gap-2 mb-8" data-testid="assistant-stats-range-filter">
                {RANGE_OPTIONS.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => setRange(o.value)}
                        className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${range === o.value ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                        data-testid={`assistant-stats-range-${o.value}`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>

            {loadingStats ? (
                <div className="flex items-center gap-2 text-gray-500" data-testid="assistant-stats-loading"><Loader2 className="w-5 h-5 animate-spin" /> Lädt Statistik …</div>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="assistant-stats-totals">
                        <StatCard icon={MousePointerClick} label="Chip-Klicks" value={totals.chip_click} testId="stat-chip-click" />
                        <StatCard icon={ArrowRight} label="CTA-Klicks" value={totals.cta_click} testId="stat-cta-click" />
                        <StatCard icon={Bell} label="Nudges gezeigt" value={totals.nudge_shown} testId="stat-nudge-shown" />
                        <StatCard icon={BellRing} label="Nudges geöffnet" value={totals.nudge_open} testId="stat-nudge-open" accent="bg-[#FFCC00]/15 border-[#FFCC00]" />
                        <StatCard icon={Bot} label="Nudge-Conversion" value={`${data?.nudge_conversion ?? 0} %`} testId="stat-nudge-conversion" />
                    </div>

                    <TrendChart trend={data?.trend} />

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                        <RankList title="Top Vorschlags-Chips" items={data?.top_chips || []} emptyText="Noch keine Chip-Klicks erfasst." testId="top-chips" />
                        <RankList title="Top Aktionen (CTA)" items={data?.top_ctas || []} emptyText="Noch keine CTA-Klicks erfasst." testId="top-ctas" />
                    </div>

                    <div className="bg-white border border-gray-200 rounded-sm p-5 mb-5" data-testid="top-nudges">
                        <h2 className="font-display text-base font-black mb-3">Nudges — gezeigt vs. geöffnet</h2>
                        {(data?.top_nudges || []).length === 0 ? (
                            <div className="text-sm text-gray-500" data-testid="top-nudges-empty">Noch keine Nudges erfasst.</div>
                        ) : (
                            <div className="space-y-3">
                                {data.top_nudges.map((n, i) => {
                                    const conv = n.shown ? Math.round((n.opened / n.shown) * 100) : 0;
                                    return (
                                        <div key={i} className="flex items-center gap-4" data-testid={`top-nudges-row-${i}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-[#111827] truncate" title={n.label}>{n.label}</div>
                                                <div className="text-[11px] text-gray-500">{n.opened} von {n.shown} geöffnet</div>
                                            </div>
                                            <div className="text-sm font-black font-mono-num w-14 text-right">{conv} %</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <RankList
                        title="Nutzung nach Rolle"
                        items={byRole.map((r) => ({ label: ROLE_LABELS[r.role] || r.role, count: r.count }))}
                        emptyText="Noch keine Daten."
                        testId="by-role"
                    />
                </>
            )}
        </div>
    );
}
